import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../integrations/supabase/client';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { CheckCircle, XCircle, AlertCircle, FileText, Clock, User } from 'lucide-react';

export default function RevisaoContratoPage() {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [tokenData, setTokenData] = useState<any>(null);
  const [contrato, setContrato] = useState<any>(null);
  const [parte, setParte] = useState<any>(null);
  const [processo, setProcesso] = useState<any>(null);
  const [comentario, setComentario] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      validateToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validar token
      if (!token) {
        setError('Token não fornecido');
        return;
      }

      const { data: tokenInfo, error: tokenError } = await supabase
        .from('tokens_revisao_contrato')
        .select('*')
        .eq('token', token)
        .single();

      if (tokenError || !tokenInfo) {
        setError('Token inválido ou expirado');
        return;
      }

      // Verificar se já foi usado
      if (tokenInfo.status !== 'pendente') {
        const reviewDate = tokenInfo.reviewed_at ? new Date(tokenInfo.reviewed_at).toLocaleDateString('pt-BR') : 'data desconhecida';
        setError(`Este token já foi ${tokenInfo.status === 'aprovado' ? 'aprovado' : 'rejeitado'} em ${reviewDate}`);
        return;
      }

      // Verificar expiração
      if (new Date(tokenInfo.expires_at) < new Date()) {
        await supabase
          .from('tokens_revisao_contrato')
          .update({ status: 'expirado' })
          .eq('id', tokenInfo.id);
        
        setError('Este token expirou');
        return;
      }

      setTokenData(tokenInfo);

      // Buscar contrato
      const { data: contratoData, error: contratoError } = await supabase
        .from('contratos_gerados')
        .select('*')
        .eq('id', tokenInfo.contrato_id)
        .single();

      if (contratoError || !contratoData) {
        setError('Contrato não encontrado');
        return;
      }

      setContrato(contratoData);

      // Buscar processo
      const { data: processoData, error: processoError } = await supabase
        .from('processos_comerciais')
        .select('*')
        .eq('id', contratoData.processo_id)
        .single();

      if (processoError || !processoData) {
        setError('Processo não encontrado');
        return;
      }

      setProcesso(processoData);

      // Buscar parte
      const { data: parteData, error: parteError } = await supabase
        .from('partes_processo')
        .select('*')
        .eq('id', tokenInfo.parte_id)
        .single();

      if (parteError || !parteData) {
        setError('Parte não encontrada');
        return;
      }

      setParte(parteData);

    } catch (err: any) {
      console.error('Erro ao validar token:', err);
      setError('Erro ao carregar dados do contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleAprovar = async () => {
    if (!tokenData || !contrato) return;

    try {
      setValidating(true);

      // Atualizar token
      await supabase
        .from('tokens_revisao_contrato')
        .update({
          status: 'aprovado',
          comentarios: comentario || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', tokenData.id);

      // Adicionar aprovação ao contrato
      const aprovacoes = contrato.aprovacoes || {};
      aprovacoes[tokenData.parte_id] = {
        parte_id: tokenData.parte_id,
        aprovado_em: new Date().toISOString(),
        comentario: comentario || null,
      };

      // Verificar se todas as partes aprovaram
      const { data: allTokens } = await supabase
        .from('tokens_revisao_contrato')
        .select('*')
        .eq('contrato_id', contrato.id);

      const todosAprovados = allTokens?.every(t => 
        t.status === 'aprovado' || t.id === tokenData.id
      );

      // Atualizar contrato
      await supabase
        .from('contratos_gerados')
        .update({
          aprovacoes,
          status: todosAprovados ? 'aprovado' : 'em_revisao',
          updated_at: new Date().toISOString(),
        })
        .eq('id', contrato.id);

      // Criar notificação para o responsável
      const { data: processoData } = await supabase
        .from('processos_comerciais')
        .select('responsavel_id')
        .eq('id', contrato.processo_id)
        .single();

      if (processoData?.responsavel_id) {
        await supabase
          .from('notifications')
          .insert({
            user_id: processoData.responsavel_id,
            type: 'contrato',
            title: todosAprovados ? 'Contrato Aprovado!' : 'Contrato Aprovado por Parte',
            message: todosAprovados 
              ? `Todas as partes aprovaram o contrato do processo ${processo?.titulo}`
              : `${parte?.nome_completo} aprovou o contrato do processo ${processo?.titulo}`,
            data: { contrato_id: contrato.id, processo_id: contrato.processo_id },
          });
      }

      toast({
        title: 'Contrato Aprovado!',
        description: 'Sua aprovação foi registrada com sucesso.',
      });

      setError('✅ Contrato aprovado com sucesso! Você pode fechar esta página.');

    } catch (err: any) {
      console.error('Erro ao aprovar:', err);
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao registrar aprovação',
      });
    } finally {
      setValidating(false);
    }
  };

  const handleRejeitar = async () => {
    if (!comentario.trim()) {
      toast({
        variant: 'destructive',
        title: 'Atenção',
        description: 'Por favor, informe o motivo da rejeição',
      });
      return;
    }

    if (!tokenData || !contrato) return;

    try {
      setValidating(true);

      // Atualizar token
      await supabase
        .from('tokens_revisao_contrato')
        .update({
          status: 'rejeitado',
          comentarios: comentario,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', tokenData.id);

      // Adicionar comentário ao contrato
      const comentarios = contrato.comentarios || [];
      comentarios.push({
        parte_id: tokenData.parte_id,
        tipo: 'ajuste_solicitado',
        comentario,
        data: new Date().toISOString(),
      });

      // Atualizar contrato
      await supabase
        .from('contratos_gerados')
        .update({
          comentarios,
          status: 'ajustes_solicitados',
          updated_at: new Date().toISOString(),
        })
        .eq('id', contrato.id);

      // Criar notificação para o responsável
      const { data: processoData } = await supabase
        .from('processos_comerciais')
        .select('responsavel_id')
        .eq('id', contrato.processo_id)
        .single();

      if (processoData?.responsavel_id) {
        await supabase
          .from('notifications')
          .insert({
            user_id: processoData.responsavel_id,
            type: 'contrato',
            title: 'Ajustes Solicitados no Contrato',
            message: `${parte?.nome_completo} solicitou ajustes no contrato do processo ${processo?.titulo}`,
            data: { contrato_id: contrato.id, processo_id: contrato.processo_id, comentario },
          });
      }

      toast({
        title: 'Ajustes Solicitados',
        description: 'Seu pedido de ajuste foi registrado.',
      });

      setError('📝 Ajustes solicitados com sucesso! O responsável será notificado.');

    } catch (err: any) {
      console.error('Erro ao rejeitar:', err);
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao registrar rejeição',
      });
    } finally {
      setValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-8 text-center">
          <Clock className="h-12 w-12 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-lg">Validando acesso...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-8 max-w-lg text-center">
          {error.startsWith('✅') || error.startsWith('📝') ? (
            <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
          ) : (
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-destructive" />
          )}
          <h1 className="text-2xl font-bold mb-4">
            {error.startsWith('✅') || error.startsWith('📝') ? 'Sucesso!' : 'Acesso Negado'}
          </h1>
          <p className="text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <Card className="p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Revisão de Contrato</h1>
              <p className="text-muted-foreground">
                Processo: <strong>{processo?.titulo}</strong>
              </p>
            </div>
            <FileText className="h-12 w-12 text-primary" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Seu Nome</p>
                <p className="font-medium">{parte?.nome_completo}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Sua Função</p>
                <Badge>{parte?.tipo_parte}</Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Versão</p>
                <p className="font-medium">v{contrato?.versao || 1}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Contrato */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Contrato</h2>
          <div 
            className="prose prose-sm max-w-none bg-muted/30 p-6 rounded-lg border"
            dangerouslySetInnerHTML={{ __html: contrato?.conteudo_html || '' }}
          />
        </Card>

        {/* Ações */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">Sua Decisão</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Comentários (opcional para aprovação, obrigatório para rejeição)
            </label>
            <textarea
              className="w-full px-4 py-3 border rounded-lg min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Adicione seus comentários aqui..."
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleAprovar}
              disabled={validating}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-5 w-5 mr-2" />
              Aprovar Contrato
            </Button>

            <Button
              onClick={handleRejeitar}
              disabled={validating || !comentario.trim()}
              variant="destructive"
              className="flex-1"
            >
              <XCircle className="h-5 w-5 mr-2" />
              Solicitar Ajustes
            </Button>
          </div>

          <p className="text-sm text-muted-foreground text-center mt-4">
            ⚠️ Esta ação não poderá ser desfeita. Certifique-se de ter lido todo o contrato antes de decidir.
          </p>
        </Card>
      </div>
    </div>
  );
}
